import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast, Uploader } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import { useUserInfo } from '@/hooks'
import { createAvatar } from '@dicebear/core'
import { identicon } from '@dicebear/collection'
import { APIWeb } from '@/api'
import { useAppState } from '@/contexts/AppStateContext'

export default React.memo(function EditProfile() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userInfo } = useUserInfo()
  const { actions } = useAppState()

  // 昵称状态 - 初始化为用户当前昵称
  const [nickname, setNickname] = useState(userInfo?.nickname || '')
  const [loading, setLoading] = useState(false)
  const [avatarFile, setAvatarFile] = useState<any[]>([])
  const uploaderRef = useRef<any>(null)
  
  // 头像文件对象
  const [avatarFileObj, setAvatarFileObj] = useState<File | null>(null)
  // 文件预览 URL（使用 createObjectURL 生成）
  const [previewUrl, setPreviewUrl] = useState<string>('')

  // 生成文件预览 URL
  useEffect(() => {
    if (avatarFileObj) {
      const url = URL.createObjectURL(avatarFileObj)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setPreviewUrl('')
    }
  }, [avatarFileObj])

  // 生成用户头像
  const avatarSvg = useMemo(() => {
    // 1. 如果有文件预览 URL，使用预览图
    if (previewUrl) {
      return previewUrl
    }

    // 2. 如果用户已有头像，使用已有头像
    if (userInfo?.avatar_url) {
      return userInfo.avatar_url
    }

    // 3. 否则使用 dicebear 生成的头像
    const seed = userInfo?.email || userInfo?.xid || 'default-user'
    const avatar = createAvatar(identicon, {
      seed: String(seed),
      size: 160,
      backgroundColor: ['#f0f0f0', '#e0e0e0', '#d0d0d0'],
    })
    return avatar.toDataUri()
  }, [previewUrl, userInfo?.email, userInfo?.xid, userInfo?.avatar_url])


  // 处理头像点击（直接触发文件选择）
  const handleAvatarClick = useCallback(() => {
    // 清空之前的文件选择，允许重新选择
    setAvatarFile([])
    
    // 延迟触发文件选择，确保状态已更新
    setTimeout(() => {
      if (uploaderRef.current) {
        const input = uploaderRef.current.querySelector('input[type="file"]') as HTMLInputElement
        if (input) {
          // 清空 input 的 value，允许选择相同的文件
          input.value = ''
          // 在移动端浏览器会显示拍照/相册选项，在桌面端会打开文件选择器
          input.click()
        }
      }
    }, 0)
  }, [])

  // 处理文件上传前的验证
  const handleBeforeRead = useCallback((file: File): File | false => {
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      Toast.info(t('请选择图片文件'))
      return false
    }

    // 验证文件大小（限制为 5MB）
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      Toast.info(t('图片大小不能超过5MB'))
      return false
    }

    return file
  }, [t])

  // 处理文件变化：保存 File 对象
  const handleFileChange = useCallback((files: any[]) => {
    // 如果清空文件，重置状态
    if (files.length === 0) {
      setAvatarFile([])
      setAvatarFileObj(null)
      return
    }
    
    const file = files[0]
    setAvatarFile(files)
    
    // 获取原始 File 对象
    const originalFile = file.file as File
    if (!originalFile) {
      Toast.fail(t('文件读取失败'))
      setAvatarFile([])
      setAvatarFileObj(null)
      return
    }
    
    // 保存 File 对象，用于提交时上传
    setAvatarFileObj(originalFile)
  }, [t])

  // 保存个人资料
  const handleSave = useCallback(async () => {
    if (loading) return

    if (!nickname.trim()) {
      Toast.info(t('请输入昵称'))
      return
    }

    try {
      setLoading(true)
      
      // 构造请求参数
      const params: EditUserInfoRequest = {
        nickname: nickname.trim(),
      }
      
      // 如果有上传头像文件，添加到参数中
      if (avatarFileObj) {
        params.avatar = avatarFileObj
      }

      // 调用 API 更新用户信息（FormData 格式）
      await APIWeb.editUserInfo(params)

      Toast.success(t('保存成功'))
      
      // 刷新用户信息
      await actions.refreshUserInfo()
      
    } catch (error: any) {
      console.error('保存个人资料失败:', error)
      // 清空已选择的文件
      setAvatarFile([])
      setAvatarFileObj(null)
    } finally {
      setLoading(false)
    }
  }, [nickname, avatarFileObj, t, actions, navigate])

  return (
    <PageLayout>
      <Header leftChildren={<Back />} centerChildren={<span className="text-3.75 font-medium text-black">{t('编辑个人资料')}</span>} />

      <div className="px-6 pt-10">
        {/* 头像区域 */}
        <div className="flex w-26 h-26 mx-auto justify-center mb-10 relative">
          <div
            className="w-26 h-26 rounded-full bg-[#D9D9D9] overflow-hidden flex items-center justify-center cursor-pointer relative"
            onClick={handleAvatarClick}
          >
            <img className="w-full h-full object-cover" src={avatarSvg} alt="Avatar" />
          </div>
          {/* 隐藏的 Uploader 组件，用于处理文件上传 */}
          <div ref={uploaderRef} style={{ display: 'none' }}>
            <Uploader
              value={avatarFile}
              onChange={handleFileChange}
              beforeRead={handleBeforeRead}
              accept="image/*"
              maxCount={1}
              previewImage={false}
            />
          </div>
          {/* 相机图标 */}
          <div className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-md">
            <img src="/icon/edit-avatar.svg" alt="Edit" />
          </div>
        </div>

        {/* 昵称输入 */}
        <div className="mb-8">
          <div className="text-sm text-[#1A1A1A] mb-2">{t('昵称')}</div>
          <CustomTextInput
            value={nickname}
            onChangeText={setNickname}
            placeholder={t('未设置昵称')}
            maxLength={20}
            showBorder={true}
            borderRadius="rounded-xl"
            borderColor="border-[#E5E5E5]"
            backgroundColor="#FFFFFF"
          />
        </div>

        {/* 保存按钮 */}
        <div className="mt-8">
          <CustomButton
            onClick={handleSave}
            loading={loading}
            disabled={loading || !nickname}
          >
            {t('保存')}
          </CustomButton>
        </div>

      </div>
    </PageLayout>
  )
})
